// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import "../styles/Events.css";
import TopNav from "../components/TopNav";
import { getUser } from "../auth/auth";
import {
  addConventionFavorite,
  getConventionFavorites,
  getConventions,
  getSavedConventionEvents,
  removeConventionFavorite,
  resolveConventionLocation,
} from "../api/conventions";
import { useToast } from "../components/ToastProvider";
import {
  groupEventsForDisplay,
  haversineMiles,
  formatLocationLabel,
} from "../utils/eventUtils";
import { buildFilteredEvents, createClearedEventFilters } from "../utils/eventsFiltering";
import LocationSelector from "../components/events/LocationSelector";
import ViewToggle from "../components/events/ViewToggle";
import FiltersSheet from "../components/events/FiltersSheet";
import ActiveFilterChips from "../components/events/ActiveFilterChips";
import EventsList from "../components/events/EventsList";
import EventsMap from "../components/events/EventsMap";
import EventPreviewSheet from "../components/events/EventPreviewSheet";
import { getRuntimeEnv } from "../config/runtimeEnv";

const DEFAULT_FILTERS = {
  distance: 25,
  dateRange: "this_month",
  customStart: "",
  customEnd: "",
  month: undefined,
  year: undefined,
  games: [],
  eventTypes: [],
  sortBy: "distance",
};

const cloneFilters = (filters) => ({
  ...filters,
  games: [...(filters.games || [])],
  eventTypes: [...(filters.eventTypes || [])],
});

const arraysEqual = (left = [], right = []) =>
  left.length === right.length && left.every((value, index) => value === right[index]);

const areFiltersEqual = (left, right) =>
  left.distance === right.distance &&
  left.dateRange === right.dateRange &&
  left.customStart === right.customStart &&
  left.customEnd === right.customEnd &&
  left.month === right.month &&
  left.year === right.year &&
  left.sortBy === right.sortBy &&
  arraysEqual(left.games, right.games) &&
  arraysEqual(left.eventTypes, right.eventTypes);

const toNumber = (value) => {
  if (value == null || value === "") return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const normalizeCountryCode = (value) => {
  const normalized = String(value || "US").trim().toUpperCase();
  return /^[A-Z]{2}$/.test(normalized) ? normalized : "US";
};

const enrichEventRecords = (records = [], location) =>
  records.map((event) => {
    const lat = event.lat ?? event.latitude;
    const lng = event.lng ?? event.longitude;
    const hasCoords = Number.isFinite(lat) && Number.isFinite(lng);
    const backendDistance = toNumber(event.distanceMiles);
    const localDistance =
      Number.isFinite(location?.lat) && Number.isFinite(location?.lng) && hasCoords
        ? haversineMiles(location.lat, location.lng, lat, lng)
        : null;
    const distance = localDistance ?? backendDistance ?? null;
    return {
      ...event,
      lat,
      lng,
      startDateTime: event.startDateTime || event.startDate,
      endDateTime: event.endDateTime || event.endDate || event.startDate,
      eventType: event.eventType || "CONVENTION",
      distance,
      distanceLabel: distance != null ? `${distance.toFixed(1)} mi` : "Unknown distance",
    };
  });

function parseFiltersFromQuery(searchParams) {
  const hasQueryParams = searchParams.toString().length > 0;
  return {
    ...DEFAULT_FILTERS,
    distance: searchParams.has("radius")
      ? toNumber(searchParams.get("radius"))
      : hasQueryParams
        ? undefined
        : DEFAULT_FILTERS.distance,
    dateRange: searchParams.get("dateRange") || (hasQueryParams ? "all" : DEFAULT_FILTERS.dateRange),
    customStart: searchParams.get("startDate") || "",
    customEnd: searchParams.get("endDate") || "",
    month: toNumber(searchParams.get("month")),
    year: toNumber(searchParams.get("year")),
    games: (searchParams.get("games") || "").split(",").map((s) => s.trim()).filter(Boolean),
    eventTypes: (searchParams.get("eventType") || "").split(",").map((s) => s.trim()).filter(Boolean),
    sortBy: searchParams.get("sort") || DEFAULT_FILTERS.sortBy,
  };
}

export default function Events() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const user = getUser();
  const { showToast } = useToast();

  const [location, setLocation] = useState(() => {
    const zipCode = searchParams.get("zipCode") || "";
    const countryCode = normalizeCountryCode(searchParams.get("countryCode") || "US");
    const lat = toNumber(searchParams.get("lat"));
    const lng = toNumber(searchParams.get("lng"));
    if (!zipCode && !Number.isFinite(lat) && !Number.isFinite(lng)) return null;
    return {
      label: zipCode ? `Postal ${zipCode}, ${countryCode}` : "Shared location",
      zipCode,
      countryCode,
      radius: toNumber(searchParams.get("radius")) || 25,
      lat,
      lng,
    };
  });
  const [countryCode, setCountryCode] = useState(() =>
    normalizeCountryCode(searchParams.get("countryCode") || "US"),
  );
  const [appliedFilters, setAppliedFilters] = useState(() => parseFiltersFromQuery(searchParams));
  const [draftFilters, setDraftFilters] = useState(() => parseFiltersFromQuery(searchParams));
  const [savedOnly, setSavedOnly] = useState(() => searchParams.get("saved") === "1");
  const [events, setEvents] = useState([]);
  const [savedEvents, setSavedEvents] = useState([]);
  const [savedOnlyEvents, setSavedOnlyEvents] = useState([]);
  const [view, setView] = useState("list");
  const [isLocating, setIsLocating] = useState(false);
  const [locationError, setLocationError] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [previewEvent, setPreviewEvent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingSavedEvents, setLoadingSavedEvents] = useState(false);
  const favoritesRequestRef = useRef(0);

  const mapApiKey = getRuntimeEnv("REACT_APP_MAPS_API_KEY");
  const mapEnabled = Boolean(mapApiKey);

  useEffect(() => {
    if (!mapEnabled && view === "map") {
      setView("list");
    }
  }, [mapEnabled, view]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (location?.zipCode) params.set("zipCode", location.zipCode);
    if (location?.countryCode) params.set("countryCode", location.countryCode);
    if (Number.isFinite(location?.lat)) params.set("lat", String(location.lat));
    if (Number.isFinite(location?.lng)) params.set("lng", String(location.lng));
    if (appliedFilters.distance != null) params.set("radius", String(appliedFilters.distance));
    if (appliedFilters.dateRange && appliedFilters.dateRange !== "all") params.set("dateRange", appliedFilters.dateRange);
    if (appliedFilters.customStart) params.set("startDate", appliedFilters.customStart);
    if (appliedFilters.customEnd) params.set("endDate", appliedFilters.customEnd);
    if (appliedFilters.month) params.set("month", String(appliedFilters.month));
    if (appliedFilters.year) params.set("year", String(appliedFilters.year));
    if (appliedFilters.games.length > 0) params.set("games", appliedFilters.games.join(","));
    if (appliedFilters.eventTypes.length > 0) params.set("eventType", appliedFilters.eventTypes.join(","));
    if (appliedFilters.sortBy && appliedFilters.sortBy !== DEFAULT_FILTERS.sortBy) params.set("sort", appliedFilters.sortBy);
    if (savedOnly) params.set("saved", "1");
    setSearchParams(params, { replace: true });
  }, [appliedFilters, location, savedOnly, setSearchParams]);

  useEffect(() => {
    if (savedOnly) {
      setLoading(false);
      return;
    }
    const load = async () => {
      setLoading(true);
      try {
        const hasLocationCenter =
          Boolean(location?.zipCode) ||
          (Number.isFinite(location?.lat) && Number.isFinite(location?.lng));
        const sortParamRaw = appliedFilters.sortBy === "soonest" ? "date_asc" : appliedFilters.sortBy;
        const sortParam = sortParamRaw === "distance" && !hasLocationCenter ? "date_asc" : sortParamRaw;
        const data = await getConventions({
          zipCode: location?.zipCode,
          countryCode: location?.countryCode || countryCode,
          lat: Number.isFinite(location?.lat) ? location.lat : undefined,
          lng: Number.isFinite(location?.lng) ? location.lng : undefined,
          radius: hasLocationCenter ? appliedFilters.distance : undefined,
          month: appliedFilters.month,
          year: appliedFilters.year,
          startDate: appliedFilters.customStart || undefined,
          endDate: appliedFilters.customEnd || undefined,
          games: appliedFilters.games.join(","),
          eventType: appliedFilters.eventTypes[0],
          sort: sortParam,
        });
        setEvents(data || []);
      } catch (error) {
        console.error("Error loading events", error);
        showToast("Failed to load events", "error");
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [savedOnly, location?.zipCode, location?.countryCode, location?.lat, location?.lng, countryCode, appliedFilters.distance, appliedFilters.month, appliedFilters.year, appliedFilters.customStart, appliedFilters.customEnd, appliedFilters.games, appliedFilters.eventTypes, appliedFilters.sortBy, showToast]);

  const loadFavoriteIds = useCallback(async () => {
    if (!user?.id) {
      setSavedEvents([]);
      return;
    }
    const requestId = ++favoritesRequestRef.current;
    try {
      const favorites = await getConventionFavorites(user.id);
      if (requestId !== favoritesRequestRef.current) return;
      setSavedEvents((favorites || []).map((item) => item.conventionId));
    } catch (error) {
      if (requestId !== favoritesRequestRef.current) return;
      console.error("Error loading favorite events", error);
    }
  }, [user?.id]);

  useEffect(() => {
    void loadFavoriteIds();
  }, [loadFavoriteIds]);

  const loadSavedEvents = useCallback(
    async ({ showErrorToast = false } = {}) => {
      if (!user?.id) {
        setSavedOnlyEvents([]);
        return;
      }
      setLoadingSavedEvents(true);
      try {
        const saved = await getSavedConventionEvents(user.id);
        setSavedOnlyEvents(saved || []);
      } catch (error) {
        console.error("Error loading saved event list", error);
        if (showErrorToast) {
          showToast("Failed to load saved events", "error");
        }
      } finally {
        setLoadingSavedEvents(false);
      }
    },
    [user?.id, showToast],
  );

  useEffect(() => {
    if (!savedOnly) {
      setLoadingSavedEvents(false);
      return;
    }
    void loadSavedEvents({ showErrorToast: true });
  }, [savedOnly, loadSavedEvents]);

  const enrichedEvents = useMemo(() => {
    return enrichEventRecords(events, location);
  }, [events, location]);

  const groupedEvents = useMemo(() => groupEventsForDisplay(enrichedEvents), [enrichedEvents]);
  const enrichedSavedOnlyEvents = useMemo(
    () => enrichEventRecords(savedOnlyEvents, location),
    [savedOnlyEvents, location],
  );
  const groupedSavedOnlyEvents = useMemo(
    () => groupEventsForDisplay(enrichedSavedOnlyEvents),
    [enrichedSavedOnlyEvents],
  );

  const savedEventIds = useMemo(
    () => new Set((savedEvents || []).map((item) => String(item))),
    [savedEvents],
  );

  const isEventSaved = useCallback(
    (event) => {
      const sourceIds =
        Array.isArray(event?.sourceIds) && event.sourceIds.length > 0
          ? event.sourceIds.map((item) => String(item))
          : [String(event?.primaryId || event?.id)];
      return sourceIds.some((sourceId) => savedEventIds.has(sourceId));
    },
    [savedEventIds],
  );

  const savedEventsCount = useMemo(() => savedEvents.length, [savedEvents]);

  const filteredEvents = useMemo(() => {
    return buildFilteredEvents({
      groupedEvents,
      groupedSavedEvents: groupedSavedOnlyEvents,
      appliedFilters,
      location,
      savedOnly,
    });
  }, [groupedEvents, groupedSavedOnlyEvents, appliedFilters, location, savedOnly]);

  const handleUseLocation = () => {
    if (!navigator.geolocation) {
      setLocationError("Geolocation is not supported in this browser.");
      return;
    }
    setIsLocating(true);
    setLocationError("");
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setIsLocating(false);
        setLocation((prev) => ({
          ...(prev ?? {}),
          label: "Current location",
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          radius: appliedFilters.distance,
          zipCode: "",
          countryCode: prev?.countryCode || countryCode,
        }));
      },
      () => {
        setIsLocating(false);
        setLocationError("Unable to fetch location. Check permissions.");
      },
    );
  };

  const handleSetLocation = async (input) => {
    if (!input) {
      setLocation(null);
      setLocationError("");
      return;
    }

    const zip = String(input).trim();
    if (zip.length < 3) {
      setLocationError("Please enter a valid postal code.");
      return;
    }

    setIsLocating(true);
    setLocationError("");
    try {
      const resolved = await resolveConventionLocation({ zipCode: zip, countryCode });
      setLocation({
        label: resolved?.label || `Postal ${zip}, ${countryCode}`,
        zipCode: resolved?.zipCode || zip,
        countryCode: resolved?.countryCode || countryCode,
        radius: appliedFilters.distance,
        lat: toNumber(resolved?.lat),
        lng: toNumber(resolved?.lng),
      });
    } catch (error) {
      console.error("Error resolving location", error);
      setLocationError("Unable to resolve that postal code for the selected country.");
    } finally {
      setIsLocating(false);
    }
  };

  const handleToggleSave = async (target) => {
    if (!user?.id) {
      showToast("Please login to save events", "error");
      return;
    }

    const event =
      target && typeof target === "object"
        ? target
        : groupedEvents.find((item) => String(item.id) === String(target)) ||
          groupedSavedOnlyEvents.find((item) => String(item.id) === String(target)) ||
          enrichedEvents.find((item) => String(item.id) === String(target));
    const sourceIds =
      Array.isArray(event?.sourceIds) && event.sourceIds.length > 0
        ? event.sourceIds.map((item) => String(item))
        : [String(event?.primaryId || event?.id || target)];
    const primaryId = String(event?.primaryId || event?.id || target);
    const isSaved = sourceIds.some((sourceId) => savedEventIds.has(sourceId));
    favoritesRequestRef.current += 1;
    try {
      if (isSaved) {
        await Promise.all(
          sourceIds
            .filter((sourceId) => savedEventIds.has(sourceId))
            .map((sourceId) => removeConventionFavorite(user.id, sourceId)),
        );
        setSavedEvents((prev) => prev.filter((item) => !sourceIds.includes(String(item))));
        setSavedOnlyEvents((prev) =>
          prev.filter((savedEvent) => {
            const savedIds =
              Array.isArray(savedEvent?.sourceIds) && savedEvent.sourceIds.length > 0
                ? savedEvent.sourceIds.map((item) => String(item))
                : [String(savedEvent?.primaryId || savedEvent?.id)];
            return !savedIds.some((savedId) => sourceIds.includes(savedId));
          }),
        );
      } else {
        await addConventionFavorite(primaryId);
        setSavedEvents((prev) => (prev.includes(primaryId) ? prev : [...prev, primaryId]));
        if (event) {
          setSavedOnlyEvents((prev) => {
            const exists = prev.some(
              (savedEvent) => String(savedEvent?.primaryId || savedEvent?.id) === primaryId,
            );
            if (exists) return prev;
            return [
              {
                ...event,
                id: primaryId,
                primaryId,
                favoriteAddedAt: new Date().toISOString(),
              },
              ...prev,
            ];
          });
        }
      }
    } catch (error) {
      console.error("Error saving event", error);
      showToast("Failed to update saved events", "error");
      void loadFavoriteIds();
    }
  };

  const handleToggleSavedOnly = () => {
    if (!user?.id) {
      showToast("Please login to view saved events", "error");
      return;
    }
    setSavedOnly((prev) => !prev);
  };

  const handleRemoveFilter = (chip) => {
    let nextApplied = cloneFilters(appliedFilters);
    if (chip.key === "distance") {
      nextApplied.distance = undefined;
    }
    if (chip.key === "dateRange") {
      nextApplied = {
        ...nextApplied,
        dateRange: "all",
        customStart: "",
        customEnd: "",
        month: undefined,
        year: undefined,
      };
    }
    if (chip.type === "games") {
      nextApplied.games = nextApplied.games.filter((game) => game !== chip.value);
    }
    if (chip.type === "eventTypes") {
      nextApplied.eventTypes = nextApplied.eventTypes.filter((type) => type !== chip.value);
    }
    if (chip.type === "month") {
      nextApplied.month = undefined;
    }
    if (chip.type === "year") {
      nextApplied.year = undefined;
    }
    setAppliedFilters(nextApplied);
    setDraftFilters(cloneFilters(nextApplied));
  };

  const handleClearFilters = () => {
    setDraftFilters(
      cloneFilters(createClearedEventFilters(draftFilters.sortBy || DEFAULT_FILTERS.sortBy)),
    );
  };

  const hasPendingFilterChanges = useMemo(
    () => !areFiltersEqual(draftFilters, appliedFilters),
    [draftFilters, appliedFilters],
  );

  const handleApplyFilters = () => {
    const nextApplied = cloneFilters(draftFilters);
    setAppliedFilters(nextApplied);
    setDraftFilters(cloneFilters(nextApplied));
    setShowFilters(false);
  };

  const handleCloseFilters = () => {
    setDraftFilters(cloneFilters(appliedFilters));
    setShowFilters(false);
  };

  const handleOpenFilters = () => {
    setDraftFilters(cloneFilters(appliedFilters));
    setShowFilters(true);
  };

  const isResultsLoading = loading || (savedOnly && loadingSavedEvents);

  return (
    <>
      <TopNav />
      <div className="events-page page-with-top-nav">
        <header className="events-header">
          <button type="button" className="back-btn" onClick={() => navigate("/home") }>
            ←
          </button>
          <div>
            <h1>Local Card Events</h1>
            <p>Trade cards in person. Meet collectors near you.</p>
          </div>
        </header>

        <LocationSelector
          location={location ? { ...location, radius: appliedFilters.distance, label: formatLocationLabel(location) } : null}
          countryCode={countryCode}
          onCountryChange={setCountryCode}
          onUseLocation={handleUseLocation}
          onSetLocation={handleSetLocation}
          onToggleSavedOnly={handleToggleSavedOnly}
          isLocating={isLocating}
          error={locationError}
          savedOnly={savedOnly}
          savedEventsCount={savedEventsCount}
          isAuthenticated={Boolean(user?.id)}
        />

        <div className="events-toolbar">
          <ViewToggle view={view} onChange={setView} mapEnabled={mapEnabled} />
          <div className="toolbar-actions">
            <button type="button" className="btn-secondary" onClick={handleOpenFilters}>
              Filters
            </button>
            <div className="sort-toggle">
              <button
                type="button"
                className={`chip ${draftFilters.sortBy === "distance" ? "active" : ""}`}
                onClick={() => setDraftFilters({ ...draftFilters, sortBy: "distance" })}
              >
                Distance
              </button>
              <button
                type="button"
                className={`chip ${draftFilters.sortBy === "soonest" ? "active" : ""}`}
                onClick={() => setDraftFilters({ ...draftFilters, sortBy: "soonest" })}
              >
                Soonest
              </button>
            </div>
            {hasPendingFilterChanges && (
              <button type="button" className="btn-secondary" onClick={handleApplyFilters}>
                Apply
              </button>
            )}
          </div>
        </div>

        {!savedOnly && <ActiveFilterChips filters={appliedFilters} onRemove={handleRemoveFilter} />}

        {isResultsLoading && (
          <div className="events-empty">
            <h3>Loading events…</h3>
          </div>
        )}

        {!isResultsLoading && filteredEvents.length === 0 && (
          <div className="events-empty">
            <h3>{savedOnly ? "No saved events yet" : "No events found"}</h3>
            <p>
              {savedOnly
                ? "Save conventions from the list or map and they’ll show up here."
                : "Try increasing distance or changing month/year filters."}
            </p>
            {!savedOnly && (
              <div className="empty-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => {
                    const nextApplied = { ...appliedFilters, distance: 500 };
                    setAppliedFilters(nextApplied);
                    setDraftFilters(cloneFilters(nextApplied));
                  }}
                >
                  Increase distance to 500 miles
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => {
                    const nextApplied = { ...appliedFilters, month: undefined, year: undefined };
                    setAppliedFilters(nextApplied);
                    setDraftFilters(cloneFilters(nextApplied));
                  }}
                >
                  Clear month/year
                </button>
              </div>
            )}
          </div>
        )}

        {!isResultsLoading && filteredEvents.length > 0 && view === "map" && mapEnabled && (
          <>
            <EventsMap
              events={filteredEvents}
              center={{ lat: location?.lat ?? 37.0902, lng: location?.lng ?? -95.7129 }}
              onSelect={setPreviewEvent}
              mapKey={mapApiKey}
            />
            <EventPreviewSheet
              event={previewEvent}
              onClose={() => setPreviewEvent(null)}
              onToggleSave={handleToggleSave}
              isSaved={previewEvent ? isEventSaved(previewEvent) : false}
            />
          </>
        )}

        {!isResultsLoading && filteredEvents.length > 0 && (view === "list" || !mapEnabled) && (
          <EventsList
            events={filteredEvents}
            onToggleSave={handleToggleSave}
            isEventSaved={isEventSaved}
          />
        )}

        <FiltersSheet
          isOpen={showFilters}
          filters={draftFilters}
          onClose={handleCloseFilters}
          onUpdate={setDraftFilters}
          onClear={handleClearFilters}
          onApply={handleApplyFilters}
        />
      </div>
    </>
  );
}
